import {
  Box,
  Divider,
  useColorModeValue,
  Text,
  VStack,
  Stack,
  Skeleton,
  useBreakpointValue,
  Center,
  Button,
  Image,
  Icon,
} from "@chakra-ui/react";
import React, { useEffect, useCallback, useState, useRef } from "react";
import { useButtonProps, useSkeletonProps, useTileProps, useUserProfile } from "hooks";
import type { Purchase } from "types/billing/UserPurchasesResponse";
import { getUserPurchases } from "api/billingPlan";
import dealsIcon from "screens/common/static/misc/deals_icon.svg";
import { useNavigate } from "react-router-dom";
import { FcTemplate } from "react-icons/fc";
import { PurchaseItem } from "screens/common/components/PurchaseItem/PurchaseItem";

const MAX_REPORTS = 5;
const REFRESH_INTERVAL = 180000; // 3 minutes

export const PurchasedReportsWatchList = () => {
  const pollingInterval = useRef<NodeJS.Timeout>();
  const cardBgColor = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const { id } = useUserProfile();
  const [isLoading, setIsLoading] = useState(true);
  const skeletonStyle = useSkeletonProps();
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const commonTileProps = useTileProps();
  const primaryButtonProps = useButtonProps("sm", "primary");
  const navigate = useNavigate();

  // Callback function to fetch purchases
  const fetchPurchases = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);

    try {
      const response = await getUserPurchases(id);
      const sortedPurchases = response
        .sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime())
        .slice(0, MAX_REPORTS);
      setPurchases(sortedPurchases);
    } catch (error) {
      setPurchases([]);
      console.error("Error fetching purchases:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPurchases();

    // TODO: replace polling with websocket or server-sent events
    pollingInterval.current = setInterval(() => {
      fetchPurchases();
    }, REFRESH_INTERVAL);

    const handleRefresh = () => {
      fetchPurchases();
    };
    window.addEventListener("refreshPurchases", handleRefresh);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      window.removeEventListener("refreshPurchases", handleRefresh);
    };
  }, [fetchPurchases]);

  return (
    <Box
      className="ch-purchased-content-list"
      {...commonTileProps}
      p="0"
      bgColor={cardBgColor}
      overflow="hidden"
      width="100%"
      height="100%"
      position="relative">
      <Stack direction="row" spacing={"1rem"} align="center" px="1rem" pt="1rem">
        <Text fontWeight="600" fontSize="md" lineHeight="1.4" color={textColor}>
          Purchased Reports
        </Text>
        {/* <Tooltip label="Refresh purchases" placement="top">
          <Button
            _hover={{}}
            _active={{}}
            color={textColor}
            size="sm"
            variant="ghost"
            paddingInlineEnd={"7px"}
            onClick={fetchPurchases}
            isLoading={isLoading}
            aria-label="Refresh purchases">
            ↻
          </Button>
        </Tooltip> */}
      </Stack>
      <VStack spacing={0} divider={<Divider borderColor={borderColor} m="0!important" />} align="stretch" minH="100px">
        {isLoading && !purchases ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={`skeleton-${index}`} {...skeletonStyle} px="2px" my="2px" borderRadius="none" height="66.4px" />
          ))
        ) : purchases && purchases.length > 0 ? (
          purchases.map((purchase) => (
            <Box key={`purchase-${purchase.id}`} px="1rem">
              <PurchaseItem purchase={purchase} />
            </Box>
          ))
        ) : (
          <Center pt="2.5rem" pb="4rem" textAlign="center">
            <Button
              {...primaryButtonProps}
              leftIcon={<Image src={dealsIcon} boxSize="1.5rem" pb="1px" />}
              onClick={() => navigate("/deal-finder")}>
              Deal Finder
            </Button>
          </Center>
        )}
      </VStack>
      <Stack
        onClick={() => navigate("/purchased-reports")}
        direction="row"
        spacing={isMobile ? ".5rem" : "1rem"}
        width="100%"
        height="2rem"
        px={"1rem"}
        py={".5rem"}
        borderTop="1px solid"
        borderColor={borderColor}
        align="center">
        <Button
          {...primaryButtonProps}
          pt="1px"
          border="none"
          paddingInline={"0"}
          _hover={{}}
          _active={{}}
          height="unset"
          leftIcon={<Icon as={FcTemplate} boxSize="1.2rem" />}
          onClick={() => navigate("/purchased-reports")}>
          View All Reports
        </Button>
      </Stack>
    </Box>
  );
};
